import { useLayoutEffect, useRef, useState } from 'react'
import debounce from 'lodash-es/debounce'

import { EMPTY_LIST, EMPTY_OBJECT } from '../constants/common'
import { isEllipsis } from '../utils/common'
import { setRef } from '../utils/react'

export function useContentOverflow({
  callback,
  dep = EMPTY_LIST,
  externalRef,
} = EMPTY_OBJECT) {
  const [overflow, setOverflow] = useState(false)

  const ref = useRef(null)

  function trigger() {
    const { current } = ref

    // The optional-chained form `current?.offsetHeight < current?.scrollHeight`
    // hits an upstream compiler bug ("Unexpected terminal kind `optional` for
    // logical test block") and bails the whole file.
    const hasOverflow = current
      ? current.offsetHeight < current.scrollHeight || isEllipsis(ref)
      : isEllipsis(ref)

    setOverflow(hasOverflow)

    callback?.(hasOverflow)
  }

  // React 19 ref callback with cleanup: the ResizeObserver attaches to
  // whichever node actually mounts (including nodes mounted late or swapped)
  // and detaches with it, so there is no mount-time ref.current race.
  // debounce invokes the wrapped function from its own timer, never during
  // render.
  function setContentOverflowRef(node) {
    ref.current = node

    setRef(externalRef, node)

    if (!node) {
      return undefined
    }

    const debouncedCheck = debounce(trigger, 50)

    const observer = new ResizeObserver(() => debouncedCheck())

    observer.observe(node)

    return () => {
      debouncedCheck.cancel()
      observer.disconnect()

      ref.current = null

      setRef(externalRef, null)
    }
  }

  useLayoutEffect(() => {
    if (ref.current) {
      trigger()
    }
  }, [...dep, callback, ref])

  return { overflow, ref, setContentOverflowRef }
}
